import { useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Accordion from '@mui/material/Accordion'
import Badge from '@mui/material/Badge'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { SCHOOL_PERMISSIONS, instructorGate, isGroundExercise, formatMinutes, resolveFlightClock, type EnrolmentProgress, type LessonStatus, type TrainingFlight } from '@jupi/shared'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError, ConfirmDialog, useClubToday, formatDate, formatDateTime, formatHours, IconButton, RemoteContent, Title, useAuth, useSnackbar } from '@jupi/ui'
import LessonBriefing from '../../components/LessonBriefing'
import { useLessonBookings, useLicencesOf, useProgress, useTrainingFlights, useTrainingMutations, type LessonBooking } from '../../api/school'

// One student's training on one course: where they are, the syllabus with
// what is done, their flights, and the pre-solo sign-offs. The student
// sees it read-only; their instructors log flights from here.

const API_BASE_URL = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? ''
const day = (iso: string) => formatDate(`${iso}T12:00:00Z`)
const STATUS_COLOR: Record<LessonStatus, 'default' | 'success' | 'warning'> = {
  not_started: 'default',
  partial: 'warning',
  completed: 'success',
  needs_repeat: 'warning',
}

export default function TrainingRecordPage() {
  const { t } = useTranslation()
  const { enrolmentId: raw } = useParams()
  const enrolmentId = Number(raw)
  const { user, can } = useAuth()
  const { data: progress, isLoading, error } = useProgress(enrolmentId)
  const { data: flights = [] } = useTrainingFlights(enrolmentId)
  const [tab, setTab] = useState<'syllabus' | 'lessons' | 'flights'>('syllabus')
  const { data: lessonBookings = [] } = useLessonBookings(enrolmentId)

  const enrolment = progress?.enrolment
  const isOwn = enrolment?.studentUserId === user?.id
  // The API decides; this only decides what to offer. A head of training
  // or an instructor in the department (the API also checks the course
  // privilege, which is why a privileged instructor sees this at all).
  const teaches =
    enrolment !== undefined &&
    !isOwn &&
    can(SCHOOL_PERMISSIONS[enrolment.department].manage, SCHOOL_PERMISSIONS[enrolment.department].instruct)
  const manages = enrolment !== undefined && can(SCHOOL_PERMISSIONS[enrolment.department].manage)

  // An instructor whose licence, medical or ratings for this course are not
  // valid sees only why: no progress, no logging. (A head of training can
  // always read the record; the API still refuses their flights.)
  const today = useClubToday()
  const { data: myLicences, isSuccess: licencesLoaded } = useLicencesOf(teaches && !manages ? user?.id : undefined)
  const gate = teaches && !manages && licencesLoaded ? instructorGate(myLicences, today, { courseCode: enrolment?.courseCode }) : null
  if (enrolment && gate?.blocked) {
    return (
      <Box>
        <Title label={enrolment.studentName} />
        <Alert severity="warning">
          {t('school.gateBlocked')}:{' '}
          {gate.issues.map((i) => `${t(`licence.type_${i.licenceType}`)} (${t(`school.gate_${i.reason}`)})`).join(', ')}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Title label={enrolment ? (isOwn ? enrolment.courseName : enrolment.studentName) : t('nav.myTraining')}>
        {(teaches || isOwn) && enrolment?.status === 'active' ? (
          <Button
            variant="outlined"
            component={RouterLink}
            to={`/bookings/new?enrolmentId=${enrolmentId}${isOwn ? '' : `&studentUserId=${enrolment.studentUserId}`}`}
            startIcon={<Icon icon="mdi:calendar-plus" />}
          >
            {t('school.bookLesson')}
          </Button>
        ) : null}
        {teaches && enrolment?.status === 'active' ? (
          <Button
            variant="contained"
            component={RouterLink}
            to={`/school/students/${enrolmentId}/log`}
            startIcon={<Icon icon="mdi:airplane-plus" />}
          >
            {t('school.logFlight')}
          </Button>
        ) : null}
      </Title>
      <RemoteContent isLoading={isLoading} error={error}>
        {progress ? (
          <Stack spacing={2}>
            <SummaryCard progress={progress} showCourse={!isOwn} />
            {progress.modules.some((m) => m.lessons.some((l) => l.gateType === 'pre_solo')) ? (
              <PreSoloCard progress={progress} enrolmentId={enrolmentId} teaches={teaches} manages={manages} />
            ) : null}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
              <Tab value="syllabus" label={t('school.syllabus')} />
              <Tab value="lessons" label={`${t('school.lessons')} (${lessonBookings.length})`} />
              <Tab value="flights" label={`${t('school.flights')} (${flights.length})`} />
            </Tabs>
            {tab === 'syllabus' ? <SyllabusProgress progress={progress} enrolmentId={teaches ? enrolmentId : undefined} /> : null}
            {tab === 'lessons' ? <LessonList bookings={lessonBookings} enrolmentId={enrolmentId} teaches={teaches} /> : null}
            {tab === 'flights' ? <FlightList flights={flights} enrolmentId={enrolmentId} teaches={teaches} /> : null}

            <Button
              href={`${API_BASE_URL}/api/school/enrolments/${enrolmentId}/export.txt`}
              startIcon={<Icon icon="mdi:file-download-outline" />}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('school.exportSummary')}
            </Button>
          </Stack>
        ) : null}
      </RemoteContent>
    </Box>
  )
}

function SummaryCard({ progress, showCourse }: { progress: EnrolmentProgress; showCourse: boolean }) {
  const { t } = useTranslation()
  const e = progress.enrolment
  return (
    <Card variant="outlined">
      <CardContent>
        {showCourse ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {e.courseName}
            {e.includesNf ? ' + NF' : ''} · {t(`school.status_${e.status}`)}
          </Typography>
        ) : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 2 }}>
          <Figure label={t('school.flown')} value={`${formatHours(progress.flownHours)} h`} />
          {e.creditedHours > 0 ? <Figure label={t('school.creditedShort')} value={`${formatHours(e.creditedHours)} h`} /> : null}
          <Figure label={t('school.lastLesson')} value={progress.lastLessonNumber ?? '–'} />
          <Figure label={t('school.solo')} value={progress.soloFlown ? t('common.yes') : t('common.no')} />
        </Box>
        {progress.completionPercent !== null ? (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                {t('school.towardsMinimum', { hours: progress.totalRequiredHours })}
              </Typography>
              <Typography variant="caption">{progress.completionPercent} %</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={progress.completionPercent} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        ) : null}
      </CardContent>
    </Card>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  )
}

// The sign-offs before the first solo. Warnings, not a hard gate -- the
// hard gate for a solo is the medical, enforced when the flight is logged.
function PreSoloCard({
  progress,
  enrolmentId,
  teaches,
  manages,
}: {
  progress: EnrolmentProgress
  enrolmentId: number
  teaches: boolean
  manages: boolean
}) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { preSolo, headTheory } = useTrainingMutations(enrolmentId)
  const check = progress.preSoloCheck
  const [percentage, setPercentage] = useState(check?.headTheoryPercentage?.toString() ?? '')
  const onError = { onError: () => showSnackbar(t('common.error'), { severity: 'error' }) }
  const by = (name: string | null, at: string | null) => (name && at ? t('school.signedBy', { name, date: formatDateTime(at) }) : '')

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('school.preSolo')}
        </Typography>
        <Stack>
          <FormControlLabel
            disabled={!teaches}
            control={
              <Checkbox
                checked={check?.theoryModule1Completed ?? false}
                onChange={(e) => preSolo.mutate({ theoryModule1Completed: e.target.checked }, onError)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{t('school.preSoloTheory')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {by(check?.theoryConfirmedByName ?? null, check?.theoryConfirmedAt ?? null)}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            disabled={!teaches}
            control={
              <Checkbox
                checked={Boolean(check?.competenceAssessedAt)}
                onChange={(e) => preSolo.mutate({ competenceAssessed: e.target.checked }, onError)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{t('school.preSoloCompetence')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {by(check?.competenceAssessedByName ?? null, check?.competenceAssessedAt ?? null)}
                </Typography>
              </Box>
            }
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, alignItems: { sm: 'center' } }}>
          <FormControlLabel
            disabled={!manages}
            control={
              <Switch checked={check?.headTheoryOk ?? false} onChange={(e) => headTheory.mutate({ headTheoryOk: e.target.checked }, onError)} />
            }
            label={t('school.headTheoryOk')}
          />
          <TextField
            size="small"
            disabled={!manages}
            label={t('school.theoryPercentage')}
            value={percentage}
            onChange={(e) => setPercentage(e.target.value.replace(/\D/g, '').slice(0, 3))}
            onBlur={() => {
              const value = percentage === '' ? null : Math.min(100, Number(percentage))
              if (value !== (check?.headTheoryPercentage ?? null)) headTheory.mutate({ headTheoryPercentage: value }, onError)
            }}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            sx={{ width: 140 }}
          />
        </Stack>
      </CardContent>
    </Card>
  )
}

function SyllabusProgress({ progress, enrolmentId }: { progress: EnrolmentProgress; enrolmentId?: number }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { groundCompleted } = useTrainingMutations(enrolmentId ?? Number.NaN)
  // A ground exercise the server would not take back without a confirmation.
  const [confirmUndo, setConfirmUndo] = useState<number | null>(null)
  const allLessons = progress.modules.flatMap((m) => m.lessons)
  const next = allLessons.find((l) => l.status !== 'completed')
  return (
    <Box>
      {next && enrolmentId ? (
        <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {t('briefings.briefNext')}: <b>{next.lessonNumber}</b> {next.title}
          </Typography>
          <LessonBriefing lessonId={next.lessonId} enrolmentId={enrolmentId} />
        </Stack>
      ) : null}
      {progress.modules.map((module) => (
        <Box key={module.moduleNumber} sx={{ mb: 2 }}>
          {progress.modules.length > 1 ? (
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('school.module', { number: module.moduleNumber })}: {module.title}
            </Typography>
          ) : null}
          {module.lessons.map((lesson) => {
            const done = lesson.items.filter((i) => i.completedVia).length
            return (
              <Accordion key={lesson.lessonId} disableGutters variant="outlined" slotProps={{ transition: { unmountOnExit: true } }}>
                <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" width={22} />}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%', minWidth: 0 }}>
                    <Typography sx={{ flex: 1, minWidth: 0 }} noWrap>
                      <b>{lesson.lessonNumber}</b> {lesson.title}
                    </Typography>
                    {lesson.items.length > 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {done}/{lesson.items.length}
                      </Typography>
                    ) : null}
                    <Chip size="small" color={STATUS_COLOR[lesson.status]} label={t(`school.lesson_${lesson.status}`)} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={0.5}>
                    {lesson.items.map((item) => (
                      <Stack key={item.itemCode} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                        <Icon
                          icon={item.completedVia ? 'mdi:check-circle' : 'mdi:checkbox-blank-circle-outline'}
                          width={18}
                          style={{ marginTop: 2, flexShrink: 0, opacity: item.completedVia ? 1 : 0.4 }}
                        />
                        <Typography variant="body2" color={item.completedVia ? 'text.primary' : 'text.secondary'}>
                          {item.description}
                          {item.completedVia === 'credit' ? ` (${t('school.creditedShort').toLowerCase()})` : ''}
                          {item.completedVia === 'ground' ? ` (${t('school.groundDone').toLowerCase()})` : ''}
                        </Typography>
                      </Stack>
                    ))}
                    <LessonBriefing lessonId={lesson.lessonId} enrolmentId={enrolmentId} />
                    {/* A ground exercise has no flight: the instructor ticks it done. */}
                    {enrolmentId && isGroundExercise(allLessons, lesson.lessonId) && (lesson.status !== 'completed' || lesson.groundCompleted) ? (
                      <Button
                        size="small"
                        variant={lesson.groundCompleted ? 'text' : 'contained'}
                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                        disabled={groundCompleted.isPending}
                        onClick={() =>
                          groundCompleted.mutate(
                            { lessonId: lesson.lessonId, completed: !lesson.groundCompleted },
                            {
                              onError: (err) =>
                                err instanceof ApiError && err.violations[0]?.code === 'ground_completion_in_use'
                                  ? setConfirmUndo(lesson.lessonId)
                                  : showSnackbar(t('common.error'), { severity: 'error' }),
                            },
                          )
                        }
                      >
                        {t(lesson.groundCompleted ? 'school.undoGroundDone' : 'school.markGroundDone')}
                      </Button>
                    ) : null}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      ))}
      <ConfirmDialog
        open={confirmUndo !== null}
        title={t('school.undoGroundTitle')}
        message={t('school.undoGroundWarning')}
        confirmLabel={t('school.undoGroundDone')}
        cancelLabel={t('common.close')}
        onConfirm={() => {
          if (confirmUndo !== null)
            groundCompleted.mutate(
              { lessonId: confirmUndo, completed: false, confirm: true },
              { onError: () => showSnackbar(t('common.error'), { severity: 'error' }) },
            )
          setConfirmUndo(null)
        }}
        onClose={() => setConfirmUndo(null)}
      />
    </Box>
  )
}

function FlightList({ flights, enrolmentId, teaches }: { flights: TrainingFlight[]; enrolmentId: number; teaches: boolean }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { deleteFlight, attach } = useTrainingMutations(enrolmentId)
  const [toDelete, setToDelete] = useState<TrainingFlight | null>(null)
  const [open, setOpen] = useState<number | null>(null)

  if (flights.length === 0) return <Typography color="text.secondary">{t('school.noFlights')}</Typography>
  return (
    <Stack spacing={1}>
      {flights.map((f) => {
        const clock = resolveFlightClock({
          offBlock: f.offBlockTime ?? '',
          takeoff: f.takeoffTime ?? '',
          landing: f.landingTime ?? '',
          onBlock: f.onBlockTime ?? '',
        })
        return (
        <Card key={f.flightId} variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>
                  {day(f.flightDate)} ·{' '}
                  {f.lessonNumber
                    ? `${[f.lessonNumber, ...f.combinedLessons.map((l) => l.lessonNumber)].join(' + ')} ${f.combinedLessons.length ? '' : (f.lessonTitle ?? '')}`.trim()
                    : t('school.extraFlight')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    clock ? `${t('school.blockTime')} ${formatMinutes(clock.blockMinutes)}` : `${formatHours(f.durationMinutes / 60)} h`,
                    clock ? `${t('school.airTime')} ${formatMinutes(clock.flightMinutes)}` : null,
                    t(`school.classification_${f.classification}`),
                    f.isCrossCountry ? t('school.crossCountry') : null,
                    f.registration,
                    f.instructorName,
                    f.items.length > 0 ? t('school.itemsDone', { count: f.items.length }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              {f.needsRepeat ? (
                <Chip size="small" color="warning" label={t('school.lesson_needs_repeat')} />
              ) : !f.fullyCompleted ? (
                <Chip size="small" color="warning" variant="outlined" label={t('school.notFullyCompleted')} />
              ) : null}
              <IconButton
                onClick={() => setOpen(open === f.flightId ? null : f.flightId)}
                aria-label={t(open === f.flightId ? 'school.hideDetails' : 'school.showDetails')}
              >
                <Icon icon={open === f.flightId ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
              </IconButton>
              <AttachmentsMenu
                flightId={f.flightId}
                count={f.attachmentCount}
                onAttach={(file) =>
                  attach.mutate({ flightId: f.flightId, file }, { onError: () => showSnackbar(t('common.error'), { severity: 'error' }) })
                }
              />
              {teaches ? (
                <IconButton onClick={() => setToDelete(f)} aria-label={t('common.delete')}>
                  <Icon icon="mdi:delete-outline" />
                </IconButton>
              ) : null}
            </Stack>
            <Collapse in={open === f.flightId} unmountOnExit>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {[
                  [t('flightLog.offBlockTime'), f.offBlockTime],
                  [t('flightLog.takeoffTime'), f.takeoffTime],
                  [t('flightLog.landingTime'), f.landingTime],
                  [t('flightLog.onBlockTime'), f.onBlockTime],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => `${k} ${v}`)
                  .join(' · ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[f.departure && f.destination ? `${f.departure} → ${f.destination}` : null, f.landings != null ? `${f.landings} ${t('flightLog.landings')}` : null, f.instrumentMinutes ? `IMC ${formatMinutes(f.instrumentMinutes)}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {f.briefingNotes ? (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {f.briefingNotes}
                </Typography>
              ) : null}
            </Collapse>
            {f.remarks ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {f.remarks}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
        )
      })}
      <ConfirmDialog
        open={toDelete !== null}
        title={t('school.deleteFlight')}
        message={t('school.deleteFlightConfirm')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.close')}
        onConfirm={() => {
          if (toDelete) deleteFlight.mutate(toDelete.flightId)
          setToDelete(null)
        }}
        onClose={() => setToDelete(null)}
      />
    </Stack>
  )
}

// A flight's files: listed when opened (not with every flight), plus an
// "add" entry -- the student may attach their own logbook photo.
function AttachmentsMenu({ flightId, count, onAttach }: { flightId: number; count: number; onAttach: (file: File) => void }) {
  const { t } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const { data: attachments = [] } = useQuery({
    queryKey: ['school', 'attachments', flightId, count],
    queryFn: () =>
      api
        .get<{ attachments: { attachmentId: number; filename: string }[] }>(`/api/school/flights/${flightId}/attachments`)
        .then((r) => r.attachments),
    enabled: anchor !== null,
  })
  return (
    <>
      <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label={t('school.attach')}>
        <Badge badgeContent={count} color="primary">
          <Icon icon="mdi:paperclip" />
        </Badge>
      </IconButton>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {attachments.map((a) => (
          <MenuItem
            key={a.attachmentId}
            component="a"
            href={`${API_BASE_URL}/api/school/flights/${flightId}/attachments/${a.attachmentId}`}
            onClick={() => setAnchor(null)}
          >
            <Icon icon="mdi:file-download-outline" style={{ marginRight: 8 }} />
            {a.filename}
          </MenuItem>
        ))}
        <MenuItem component="label">
          <Icon icon="mdi:plus" style={{ marginRight: 8 }} />
          {t('school.attach')}
          <input
            hidden
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) onAttach(file)
              e.target.value = ''
              setAnchor(null)
            }}
          />
        </MenuItem>
      </Menu>
    </>
  )
}

// Booked lessons, newest first. A past one not yet logged offers "Log
// flight", which opens the log with the booking's aircraft, lesson and
// times filled in.
function LessonList({ bookings, enrolmentId, teaches }: { bookings: LessonBooking[]; enrolmentId: number; teaches: boolean }) {
  const { t } = useTranslation()
  if (bookings.length === 0) return <Typography color="text.secondary">{t('school.noLessons')}</Typography>
  const now = Date.now()
  return (
    <Stack spacing={1}>
      {bookings.map((b) => {
        const past = new Date(b.startsAt).getTime() < now
        return (
          <Card key={b.bookingId} variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>
                  {formatDateTime(b.startsAt)} · {b.lessonNumber ? `${b.lessonNumber} ${b.lessonTitle ?? ''}` : t('school.extraFlight')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[b.registration, b.instructorName].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              {b.flightLogged ? (
                <Chip size="small" color="success" label={t('school.logged')} />
              ) : teaches && past ? (
                <Button size="small" variant="contained" component={RouterLink} to={`/school/students/${enrolmentId}/log?bookingId=${b.bookingId}`}>
                  {t('school.logFlight')}
                </Button>
              ) : !past ? (
                <Chip size="small" variant="outlined" label={t('school.upcoming')} />
              ) : null}
            </CardContent>
          </Card>
        )
      })}
    </Stack>
  )
}
